import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { KeenIcon } from '@/components';
import {
  useGetPortWatchShiftsQuery,
  useAssignShiftMemberMutation,
  useUnassignShiftMemberMutation,
  useCompleteShiftMutation,
} from '@/services/ClubResourceService';
import { useGetUsersByClubIdQuery } from '@/services/ClubService';
import {
  IPortWatchShift,
  IAssignShiftPayload,
} from '@/types/club-resource.types';
import { UserResponse } from '@/types/user-management.types';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

export const PortWatchShiftsDetails: React.FC = () => {
  const { resourceId } = useParams<{ resourceId: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // Format UTC date string to display the exact time from API without timezone conversion
  const formatUTCDate = (utcDateString: string, formatString: string) => {
    const date = new Date(utcDateString);
    // Create a new date using UTC components to avoid timezone conversion
    const utcDate = new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds()
    );
    return format(utcDate, formatString);
  };

  const [selectedShift, setSelectedShift] = useState<IPortWatchShift | null>(
    null
  );
  const [assignForm, setAssignForm] = useState({
    user_id: '',
    override: false,
  });
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: shiftsResponse,
    isLoading,
    isError,
    refetch,
  } = useGetPortWatchShiftsQuery(resourceId || '', {
    skip: !resourceId,
  });

  const [assignShiftMember, { isLoading: isAssigning }] =
    useAssignShiftMemberMutation();
  const [unassignShiftMember, { isLoading: isUnassigning }] =
    useUnassignShiftMemberMutation();
  const [completeShift, { isLoading: isCompleting }] =
    useCompleteShiftMutation();

  // Get clubId from shifts data
  const shifts: IPortWatchShift[] = shiftsResponse?.data || [];
  const clubId = shifts[0]?.club?.id;

  // Fetch users for assignment based on club_id
  const { data: usersResponse, isLoading: isLoadingUsers } =
    useGetUsersByClubIdQuery(
      {
        club_id: clubId || '',
        page: 1,
        limit: 50,
        search: searchTerm,
        status: 1, // Active users only
      },
      {
        skip: !clubId, // Skip the query if clubId is not available
      }
    );

  const handleAssignMember = async () => {
    if (!assignForm.user_id || !selectedShift) {
      enqueueSnackbar('Please select a user', { variant: 'error' });
      return;
    }

    try {
      const payload: IAssignShiftPayload = {
        user_id: assignForm.user_id,
        override: assignForm.override,
      };
      await assignShiftMember({ shiftId: selectedShift.id, payload }).unwrap();
      enqueueSnackbar('Member assigned successfully', { variant: 'success' });
      setSelectedShift(null);
      setAssignForm({ user_id: '', override: false });
      setShowAssignModal(false);
      refetch();
    } catch {
      enqueueSnackbar('Failed to assign member', { variant: 'error' });
    }
  };

  const handleUnassignMember = async (shiftId: string) => {
    try {
      await unassignShiftMember(shiftId).unwrap();
      enqueueSnackbar('Member unassigned successfully', { variant: 'success' });
      refetch();
    } catch {
      enqueueSnackbar('Failed to unassign member', { variant: 'error' });
    }
  };

  const handleCompleteShift = async (shiftId: string) => {
    try {
      await completeShift(shiftId).unwrap();
      enqueueSnackbar('Shift marked as completed successfully', {
        variant: 'success',
      });
      refetch();
    } catch {
      enqueueSnackbar('Failed to complete shift', { variant: 'error' });
    }
  };

  const openAssignModal = (shift: IPortWatchShift) => {
    setSelectedShift(shift);
    setShowAssignModal(true);
    setAssignForm({ user_id: '', override: false });
  };

  const closeAssignModal = () => {
    setShowAssignModal(false);
    setSelectedShift(null);
    setAssignForm({ user_id: '', override: false });
    setSearchTerm('');
  };

  const filteredUsers: UserResponse[] = Array.isArray(usersResponse?.data)
    ? usersResponse.data
    : [];

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showAssignModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showAssignModal]);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <span className="ml-3 text-lg">Loading shifts...</span>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-16">
          <KeenIcon
            type="filled"
            icon="exclamation-triangle"
            className="text-red-500 text-6xl mb-6"
          />
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            Failed to load shifts data
          </h2>
          <p className="text-gray-600 mb-6">
            There was an error loading the port watch shifts information.
          </p>
          <div className="flex items-center justify-center gap-4">
            <button onClick={() => refetch()} className="btn btn-primary">
              <KeenIcon type="filled" icon="arrows-circle" className="mr-2" />
              Retry
            </button>
            <button onClick={() => navigate(-1)} className="btn btn-light">
              <KeenIcon type="filled" icon="arrow-left" className="mr-2" />
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const resourceName = shifts[0]?.resource?.name || 'Port Watch Resource';

  // Navigate back to club details with club resources tab active
  const handleBackToClub = () => {
    if (clubId) {
      navigate(`/club-management/${clubId}`, {
        state: { from: 'club-resources' },
      });
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBackToClub}
            className="btn btn-sm btn-light btn-icon"
            title="Go back"
          >
            <KeenIcon type="filled" icon="arrow-left" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Port Watch Shifts
            </h1>
            <p className="text-gray-600 mt-1">
              Manage shifts and member assignments
            </p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          className="btn btn-sm btn-light"
          title="Refresh data"
        >
          <KeenIcon type="filled" icon="arrows-circle" className="mr-2" />
          Refresh
        </button>
      </div>

      {/* Resource Info Card */}
      <div className="card mb-8">
        <div className="card-header">
          <h2 className="card-title">Resource Information</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-950">
                <KeenIcon
                  type="filled"
                  icon="calendar"
                  className="text-blue-600 dark:text-blue-200 text-xl"
                />
              </div>
              <div>
                <p className="text-sm text-gray-600">Resource Name</p>
                <p className="font-semibold text-gray-900">{resourceName}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-green-100 dark:bg-green-950">
                <KeenIcon
                  type="filled"
                  icon="users"
                  className="text-green-600 dark:text-green-200 text-xl"
                />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Shifts</p>
                <p className="font-semibold text-gray-900">{shifts.length}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-orange-100 dark:bg-orange-950">
                <KeenIcon
                  type="filled"
                  icon="user-check"
                  className="text-orange-600 dark:text-orange-200 text-xl"
                />
              </div>
              <div>
                <p className="text-sm text-gray-600">Assigned Shifts</p>
                <p className="font-semibold text-gray-900">
                  {shifts.filter((shift) => shift.assigned_member).length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Shifts List */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Shifts Schedule</h2>
        </div>
        <div className="card-body">
          {shifts.length === 0 ? (
            <div className="text-center py-16">
              <KeenIcon
                type="filled"
                icon="calendar"
                className="text-gray-400 text-6xl mb-6"
              />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No shifts found
              </h3>
              <p className="text-gray-600">
                There are no shifts scheduled for this resource.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {shifts.map((shift: IPortWatchShift) => (
                <div
                  key={shift.id}
                  className="border border-gray-200 rounded-lg p-6 hover:bg-gray-200 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-6 mb-4">
                        <div className="flex items-center gap-3">
                          <KeenIcon
                            type="filled"
                            icon="calendar"
                            className="text-blue-500 text-lg"
                          />
                          <div>
                            <p className="text-sm text-gray-600">
                              Start Date & Time
                            </p>
                            <p className="font-semibold text-gray-900">
                              {formatUTCDate(
                                shift.shift_start,
                                "MMM dd, yyyy 'at' HH:mm"
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <KeenIcon
                            type="filled"
                            icon="clock"
                            className="text-gray-500 text-lg"
                          />
                          <div>
                            <p className="text-sm text-gray-600">
                              End Date & Time
                            </p>
                            <p className="font-semibold text-gray-900">
                              {formatUTCDate(
                                shift.shift_end,
                                "MMM dd, yyyy 'at' HH:mm"
                              )}
                            </p>
                          </div>
                        </div>
                        {shift.completed_at && (
                          <div className="flex items-center gap-3">
                            <KeenIcon
                              type="filled"
                              icon="check"
                              className="text-green-600 text-lg"
                            />
                            <div>
                              <p className="text-sm text-gray-600">
                                Completed At (UTC)
                              </p>
                              <p className="font-semibold text-gray-900">
                                {formatUTCDate(
                                  shift.completed_at,
                                  "MMM dd, yyyy 'at' HH:mm 'UTC'"
                                )}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Assigned Member */}
                      <div className="flex items-center gap-3">
                        <KeenIcon
                          type="filled"
                          icon="user"
                          className="text-gray-500 text-lg"
                        />
                        <div>
                          <p className="text-sm text-gray-600">
                            Assigned Member
                          </p>
                          {shift.assigned_member ? (
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-green-600">
                                {shift.assigned_member.name}
                              </span>
                              <span className="text-sm text-gray-500">
                                ({shift.assigned_member.email})
                              </span>
                            </div>
                          ) : (
                            <span className="font-semibold text-orange-600">
                              No member assigned
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3">
                      {shift.assigned_member ? (
                        <>
                          {!shift.completed_at && (
                            <button
                              onClick={() => openAssignModal(shift)}
                              className="btn btn-sm btn-primary"
                              disabled={isAssigning}
                            >
                              <KeenIcon
                                type="filled"
                                icon="user-edit"
                                className="mr-2"
                              />
                              Reassign
                            </button>
                          )}
                          {!shift.completed_at && (
                            <button
                              onClick={() => handleUnassignMember(shift.id)}
                              className="btn btn-sm btn-light"
                              disabled={isUnassigning}
                            >
                              <KeenIcon
                                type="filled"
                                icon="user-minus"
                                className="mr-2"
                              />
                              Unassign
                            </button>
                          )}
                          {!shift.completed_at && (
                            <button
                              onClick={() => handleCompleteShift(shift.id)}
                              className="btn btn-sm btn-success"
                              disabled={isCompleting}
                            >
                              <KeenIcon
                                type="filled"
                                icon="check"
                                className="mr-2"
                              />
                              {isCompleting ? 'Completing...' : 'Complete'}
                            </button>
                          )}
                        </>
                      ) : (
                        !shift.completed_at && (
                          <button
                            onClick={() => openAssignModal(shift)}
                            className="btn btn-sm btn-primary"
                            disabled={isAssigning}
                          >
                            <KeenIcon
                              type="filled"
                              icon="user-plus"
                              className="mr-2"
                            />
                            Assign Member
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* User Selection Modal */}
      {showAssignModal && selectedShift && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <div className="bg-gray-100 rounded-lg p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                {selectedShift.assigned_member
                  ? 'Reassign Member'
                  : 'Assign Member to Shift'}
              </h3>
              <button
                onClick={closeAssignModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <KeenIcon type="filled" icon="cross" className="text-xl" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Search Users */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Users
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full input px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search by name or email..."
                />
              </div>

              {/* User List */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select User *
                </label>
                <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-md">
                  {isLoadingUsers ? (
                    <div className="p-4 text-center text-gray-500">
                      Loading users...
                    </div>
                  ) : filteredUsers.length > 0 ? (
                    <div className="space-y-1">
                      {filteredUsers.map((user: UserResponse) => (
                        <div
                          key={user.id}
                          onClick={() =>
                            setAssignForm((prev) => ({
                              ...prev,
                              user_id: user.id,
                            }))
                          }
                          className={`p-3 cursor-pointer hover:bg-gray-200 border-b border-gray-100 ${
                            assignForm.user_id === user.id
                              ? 'bg-blue-50 dark:bg-blue-900 border-blue-200'
                              : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900">
                                {user.name}
                              </p>
                              <p className="text-sm text-gray-500">
                                {user.email}
                              </p>
                            </div>
                            {assignForm.user_id === user.id && (
                              <KeenIcon
                                type="filled"
                                icon="check"
                                className="text-blue-600"
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      No users found
                    </div>
                  )}
                </div>
              </div>

              {/* Override checkbox for reassignment */}
              {selectedShift.assigned_member && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="override"
                    checked={assignForm.override}
                    onChange={(e) =>
                      setAssignForm((prev) => ({
                        ...prev,
                        override: e.target.checked,
                      }))
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <label
                    htmlFor="override"
                    className="text-sm text-gray-700 cursor-pointer"
                  >
                    Override existing assignment
                  </label>
                </div>
              )}

              <div className="flex items-center gap-3 pt-4">
                <button
                  onClick={handleAssignMember}
                  disabled={isAssigning || !assignForm.user_id}
                  className="btn btn-primary flex-1"
                >
                  {isAssigning
                    ? 'Assigning...'
                    : selectedShift.assigned_member
                      ? 'Reassign'
                      : 'Assign Member'}
                </button>
                <button
                  onClick={closeAssignModal}
                  className="btn btn-light flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
